import type {
  HilOpportunityType,
  HumanInTheLoopSection,
} from 'app/pages/HumanInTheLoopPage/types';

export const HUMAN_IN_THE_LOOP_BASE_PATH = '/human-in-the-loop';
export const HUMAN_IN_THE_LOOP_VIEW_PARAM = 'view';
export const HUMAN_IN_THE_LOOP_SEGMENT_NAME_PARAM = 'metadata.segment_name';
export const HUMAN_IN_THE_LOOP_OPPORTUNITY_TYPE_PARAM = 'opportunity_type';

const DEFAULT_HIL_VIEW: HumanInTheLoopSection = 'templates_pending';
const DEFAULT_HIL_OPPORTUNITY_TYPE: HilOpportunityType = 'test';

export type BuildHumanInTheLoopPathOptions = {
  segmentName?: string;
  opportunityType?: HilOpportunityType;
};

export function parseHumanInTheLoopViewFromSearch(
  search: string,
): HumanInTheLoopSection {
  const view = new URLSearchParams(search).get(HUMAN_IN_THE_LOOP_VIEW_PARAM);
  if (view === 'segments_attention') return 'segments_attention';
  if (view === 'templates_pending') return 'templates_pending';
  return DEFAULT_HIL_VIEW;
}

export function parseHilOpportunityTypeFromSearch(
  search: string,
): HilOpportunityType {
  const raw = new URLSearchParams(search)
    .get(HUMAN_IN_THE_LOOP_OPPORTUNITY_TYPE_PARAM)
    ?.trim()
    .toLowerCase();
  if (raw === 'active' || raw === 'live') return 'active';
  if (raw === 'test') return 'test';
  return DEFAULT_HIL_OPPORTUNITY_TYPE;
}

export function parseSegmentNameFromSearch(search: string): string {
  const raw = new URLSearchParams(search).get(HUMAN_IN_THE_LOOP_SEGMENT_NAME_PARAM);
  return raw?.trim() ?? '';
}

export function buildHumanInTheLoopPath(
  view: HumanInTheLoopSection = DEFAULT_HIL_VIEW,
  options?: BuildHumanInTheLoopPathOptions,
): string {
  const params = new URLSearchParams();
  params.set(HUMAN_IN_THE_LOOP_VIEW_PARAM, view);
  const opportunityType = options?.opportunityType ?? DEFAULT_HIL_OPPORTUNITY_TYPE;
  params.set(HUMAN_IN_THE_LOOP_OPPORTUNITY_TYPE_PARAM, opportunityType);
  const segmentName = options?.segmentName?.trim() ?? '';
  if (segmentName) {
    params.set(HUMAN_IN_THE_LOOP_SEGMENT_NAME_PARAM, segmentName);
  }
  return `${HUMAN_IN_THE_LOOP_BASE_PATH}?${params.toString()}`;
}

export type HumanInTheLoopBreadcrumb = {
  label: string;
  link?: string;
  isActive?: boolean;
};

export type HumanInTheLoopBreadcrumbState = {
  breadcrumbParentLabel?: string;
  breadcrumbParentPath?: string;
  /** Set when navigating from segments redirect action to filtered templates. */
  redirectFromSection?: HumanInTheLoopSection;
  breadcrumbsList?: HumanInTheLoopBreadcrumb[];
};

export type BuildHumanInTheLoopBreadcrumbsOptions = {
  hilLabel?: string;
  redirectFromSection?: HumanInTheLoopSection;
  clearRedirectFromSection?: boolean;
};

const HIL_SECTION_LABELS: Record<HumanInTheLoopSection, string> = {
  templates_pending: 'Generated Templates Pending Approval',
  segments_attention: 'Segments Needing Attention',
};

export function getHumanInTheLoopSectionLabel(
  section: HumanInTheLoopSection,
): string {
  return HIL_SECTION_LABELS[section];
}

export function buildHumanInTheLoopBreadcrumbs(
  view: HumanInTheLoopSection,
  state?: HumanInTheLoopBreadcrumbState | null,
  pathOptions?: BuildHumanInTheLoopPathOptions,
  options?: BuildHumanInTheLoopBreadcrumbsOptions,
): HumanInTheLoopBreadcrumb[] {
  const hilLabel = options?.hilLabel ?? 'HIL';
  const redirectFromSection = options?.clearRedirectFromSection
    ? undefined
    : options?.redirectFromSection ?? state?.redirectFromSection;
  const showRedirectCrumb =
    view === 'templates_pending' &&
    redirectFromSection === 'segments_attention';
  const crumbs: HumanInTheLoopBreadcrumb[] = [];

  if (state?.breadcrumbParentLabel && state?.breadcrumbParentPath) {
    crumbs.push({
      label: state.breadcrumbParentLabel,
      link: state.breadcrumbParentPath,
      isActive: false,
    });
  }

  const opportunityTypeOnly: BuildHumanInTheLoopPathOptions | undefined =
    pathOptions?.opportunityType
      ? { opportunityType: pathOptions.opportunityType }
      : undefined;

  crumbs.push({
    label: hilLabel,
    link: buildHumanInTheLoopPath(
      showRedirectCrumb ? 'templates_pending' : view,
      showRedirectCrumb ? opportunityTypeOnly : pathOptions,
    ),
    isActive: false,
  });

  if (showRedirectCrumb && redirectFromSection) {
    crumbs.push({
      label: getHumanInTheLoopSectionLabel(redirectFromSection),
      link: buildHumanInTheLoopPath(redirectFromSection, opportunityTypeOnly),
      isActive: false,
    });
  }

  crumbs.push({
    label: getHumanInTheLoopSectionLabel(view),
    isActive: true,
  });

  return crumbs;
}

export function buildHumanInTheLoopNavigationState(
  view: HumanInTheLoopSection,
  existingState?: HumanInTheLoopBreadcrumbState | null,
  pathOptions?: BuildHumanInTheLoopPathOptions,
  options?: BuildHumanInTheLoopBreadcrumbsOptions,
): HumanInTheLoopBreadcrumbState {
  const redirectFromSection = options?.clearRedirectFromSection
    ? undefined
    : options?.redirectFromSection ?? existingState?.redirectFromSection;
  const breadcrumbsList = buildHumanInTheLoopBreadcrumbs(
    view,
    existingState,
    pathOptions,
    options,
  );
  return {
    breadcrumbParentLabel: existingState?.breadcrumbParentLabel,
    breadcrumbParentPath: existingState?.breadcrumbParentPath,
    redirectFromSection,
    breadcrumbsList,
  };
}
